import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Table, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import axiosInstance from '../../services/axiosConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { calculateAge } from '../../utils/dateUtils';
import { resolveMemberBirthDate } from '../../utils/fiscalCodeUtils';

const TeamDocuments = () => {
  const { id } = useParams();
  const eventId = id; // Route uses :id parameter
  const navigate = useNavigate();

  // State
  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);

  // Fetch data
  useEffect(() => {
    fetchEventDetails();
    fetchTeams();
    fetchAssociations();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvent(response.data.event || response.data);
    } catch (err) {
      console.error('Errore caricamento evento:', err);
      setError('Impossibile caricare i dettagli dell\'evento');
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axiosInstance.get(`/api/events/${eventId}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success) {
        const teamsData = response.data.teams || [];
        console.log('Teams caricati:', teamsData.length);
        setTeams(teamsData);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Errore caricamento squadre:', err);
      setError('Impossibile caricare le squadre');
      setLoading(false);
    }
  };

  const fetchAssociations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/association-data', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.associations) {
        setAssociations(response.data.associations);
      }
    } catch (err) {
      console.error('Errore caricamento associazioni:', err);
    }
  };

  const getAssociationName = (associationId) => {
    const association = associations.find(a => a.id === parseInt(associationId));
    return association ? association.name : `Associazione ${associationId}`;
  };

  const sanitizeFolderName = (value) => {
    return String(value || 'N_A')
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .trim() || 'N_A';
  };

  const normalizeDurationValue = (value) => {
    if (typeof value !== 'string') return value;

    // Normalize frequent typo where users type ! instead of 1.
    const normalizedInput = value.replace(/!/g, '1').trim().toLowerCase();
    if (!normalizedInput) return value;

    const toMMSS = (minutes, seconds = 0) => {
      const safeMinutes = Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0;
      const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
      const totalSeconds = safeMinutes * 60 + safeSeconds;
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const ss = String(totalSeconds % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    };

    // Already in m:ss or mm:ss style.
    const colonMatch = normalizedInput.match(/^(\d{1,3})\s*[:.]\s*(\d{1,2})$/);
    if (colonMatch) {
      return toMMSS(parseInt(colonMatch[1], 10), parseInt(colonMatch[2], 10));
    }

    // Natural language: "1m e 30secondi", "2 min 5 sec", "1 minuto".
    const minuteMatch = normalizedInput.match(/(\d{1,3})\s*(m|min|mins|minuto|minuti)\b/);
    const secondMatch = normalizedInput.match(/(\d{1,3})\s*(s|sec|secs|secondo|secondi)\b/);
    if (minuteMatch || secondMatch) {
      const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
      const seconds = secondMatch ? parseInt(secondMatch[1], 10) : 0;
      return toMMSS(minutes, seconds);
    }

    // Compact digits: "130" => 01:30, "90" => 01:30, "1" => 01:00.
    if (/^\d{1,4}$/.test(normalizedInput)) {
      const numeric = parseInt(normalizedInput, 10);
      if (normalizedInput.length >= 3) {
        const minutes = parseInt(normalizedInput.slice(0, -2), 10);
        const seconds = parseInt(normalizedInput.slice(-2), 10);
        return toMMSS(minutes, seconds);
      }
      return toMMSS(0, numeric);
    }

    // Last attempt: extract first two numeric groups, e.g. "1m e 30".
    const numbers = normalizedInput.match(/\d+/g);
    if (numbers && numbers.length > 0) {
      const minutes = parseInt(numbers[0], 10);
      const seconds = numbers.length > 1 ? parseInt(numbers[1], 10) : 0;
      return toMMSS(minutes, seconds);
    }

    return normalizedInput;
  };

  const formatFormFieldValue = (key, value) => {
    if (key === 'choreographyEffectiveDuration') {
      return normalizeDurationValue(value);
    }
    return value;
  };

  const getFormFieldLabel = (key) => {
    if (key === 'style') return 'Stile';
    return key;
  };

  const sanitizeSheetName = (value) => {
    return String(value || 'Moduli')
      .replace(/[\\/?*\[\]:]/g, ' ')
      .trim()
      .slice(0, 31) || 'Moduli';
  };

  const getTeamFolderName = (team) => {
    const category = team.formData?.category || 'categoria';
    const style = team.formData?.style || 'stile';
    const title = team.formData?.choreographyTitle || team.name || 'coreografia';

    return sanitizeFolderName(`${category}_${style}_${title}`);
  };

  const formatMemberBirthWithAge = (member) => {
    if (member?.age !== undefined && member?.age !== null && member?.age !== '') {
      return String(member.age);
    }

    const birthDateIso = resolveMemberBirthDate(member);
    if (!birthDateIso) return 'N/A';

    const birthDate = new Date(birthDateIso);
    if (Number.isNaN(birthDate.getTime())) return 'N/A';

    const day = String(birthDate.getDate()).padStart(2, '0');
    const month = String(birthDate.getMonth() + 1).padStart(2, '0');
    const year = birthDate.getFullYear();
    const age = calculateAge(birthDateIso);

    return age != null
      ? `${day}/${month}/${year} (${age} anni)`
      : `${day}/${month}/${year}`;
  };

  const toggleTeamSelection = (teamId) => {
    setSelectedTeams(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const selectAllTeams = () => {
    if (selectedTeams.length === teams.length) {
      setSelectedTeams([]);
    } else {
      setSelectedTeams(teams.map(t => t._id));
    }
  };

  const getTeamFiles = (team) => {
    const files = [];
    
    // Check formData for file objects
    if (team.formData && typeof team.formData === 'object') {
      Object.entries(team.formData).forEach(([key, value]) => {
        if (value && typeof value === 'object' && value.filename) {
          files.push({
            fieldName: key,
            filename: value.filename,
            type: value.type || 'file',
            mimetype: value.mimetype,
            path: value.path
          });
        }
      });
    }
    
    return files;
  };

  const downloadTeamDocument = async (team, file) => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      // Download the file from the server
      const response = await axiosInstance.get(
        `/api/events/${eventId}/teams/${team._id}/files/${file.fieldName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(`File "${file.filename}" scaricato con successo`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Errore download file:', err);
      setError(`Impossibile scaricare il file "${file.filename}"`);
    } finally {
      setDownloading(false);
    }
  };

  const exportAllDocuments = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      // Filter selected teams
      const teamsToExport = teams.filter(t => selectedTeams.includes(t._id));
      
      if (teamsToExport.length === 0) {
        setError('Seleziona almeno una squadra');
        setDownloading(false);
        return;
      }

      const zip = new JSZip();
      
      // Add team info file
      let teamInfoContent = "SQUADRE ESPORTATE\n";
      teamInfoContent += "=".repeat(50) + "\n\n";
      teamInfoContent += `Evento: ${event?.title || 'N/A'}\n`;
      teamInfoContent += `Data Export: ${new Date().toLocaleString('it-IT')}\n\n`;
      
      for (const team of teamsToExport) {
        const associationName = getAssociationName(team.association_id);
        const associationFolder = zip.folder(sanitizeFolderName(associationName));
        const teamFolderName = getTeamFolderName(team);
        const teamFolder = associationFolder.folder(teamFolderName);
        
        // Add team info
        teamInfoContent += `\nSquadra: ${team.name}\n`;
        teamInfoContent += `Associazione: ${associationName}\n`;
        teamInfoContent += `Membri: ${team.members.length}\n`;
        
        // Create team details file
        let teamDetails = `DETTAGLI SQUADRA: ${team.name}\n`;
        teamDetails += "=".repeat(50) + "\n\n";
        teamDetails += `Associazione: ${associationName}\n`;
        teamDetails += `Data Iscrizione: ${new Date(team.registration_date).toLocaleString('it-IT')}\n`;
        teamDetails += `Numero Membri: ${team.members.length}\n\n`;
        
        // Add form data
        if (team.formData && Object.keys(team.formData).length > 0) {
          teamDetails += "INFORMAZIONI ISCRIZIONE:\n";
          teamDetails += "-".repeat(50) + "\n";
          Object.entries(team.formData).forEach(([key, value]) => {
            if (typeof value !== 'object' || value === null) {
              const formattedValue = formatFormFieldValue(key, value);
              const fieldLabel = getFormFieldLabel(key);
              teamDetails += `${fieldLabel}: ${formattedValue}\n`;
            } else if (value.filename) {
              const fieldLabel = getFormFieldLabel(key);
              teamDetails += `${fieldLabel}: ${value.filename} (file allegato)\n`;
            }
          });
          teamDetails += "\n";
        }
        
        // Add members list
        teamDetails += "MEMBRI:\n";
        teamDetails += "-".repeat(50) + "\n";
        team.members.forEach((member, idx) => {
          teamDetails += `${idx + 1}. ${member.first_name} ${member.last_name}\n`;
          teamDetails += `   Data di nascita (Età): ${formatMemberBirthWithAge(member)}\n`;
          teamDetails += `   CF: ${member.fiscal_code || 'N/A'}\n`;
          teamDetails += `   Email: ${member.email || 'N/A'}\n`;
          teamDetails += `   Telefono: ${member.phone || 'N/A'}\n\n`;
        });
        
        teamFolder.file("dettagli_squadra.txt", teamDetails);
        
        // Download and add files
        const files = getTeamFiles(team);
        if (files.length > 0) {
          teamInfoContent += `File allegati: ${files.length}\n`;
          
          for (const file of files) {
            try {
              const response = await axiosInstance.get(
                `/api/events/${eventId}/teams/${team._id}/files/${file.fieldName}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob'
                }
              );
              
              teamFolder.file(file.filename, response.data);
            } catch (err) {
              console.error(`Errore download file ${file.filename}:`, err);
              teamInfoContent += `  - ${file.filename} (ERRORE)\n`;
            }
          }
        } else {
          teamInfoContent += `File allegati: Nessuno\n`;
        }
      }
      
      // Add main info file
      zip.file("ELENCO_SQUADRE.txt", teamInfoContent);
      
      // Generate ZIP
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${event?.title || 'evento'}_documenti_${new Date().toISOString().split('T')[0]}.zip`);
      
      setSuccess(`Export completato! ${teamsToExport.length} squadre esportate`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Errore export documenti:', err);
      setError('Impossibile completare l\'export');
    } finally {
      setDownloading(false);
    }
  };

  const exportRegistrationForms = async () => {
    try {
      setDownloading(true);
      
      // Filter selected teams
      const teamsToExport = teams.filter(t => selectedTeams.includes(t._id));
      
      if (teamsToExport.length === 0) {
        setError('Seleziona almeno una squadra');
        setDownloading(false);
        return;
      }

      // Get all unique form fields
      const allFields = new Set();
      teamsToExport.forEach(team => {
        if (team.formData) {
          Object.keys(team.formData).forEach(key => {
            if (typeof team.formData[key] !== 'object') {
              allFields.add(key);
            }
          });
        }
      });
      
      const fieldArray = Array.from(allFields);

      const headerRow = [
        'Nome Squadra',
        'Associazione',
        'Data Iscrizione',
        'N. Membri',
        ...fieldArray.map(field => getFormFieldLabel(field))
      ];

      const dataRows = teamsToExport.map(team => {
        const association = getAssociationName(team.association_id);
        const date = new Date(team.registration_date).toLocaleDateString('it-IT');

        const formValues = fieldArray.map((field) => {
          const rawValue = team.formData?.[field] || 'N/A';
          return formatFormFieldValue(field, rawValue);
        });

        return [team.name, association, date, team.members.length, ...formValues];
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      worksheet['!cols'] = headerRow.map((_, index) => {
        const maxLength = [headerRow[index], ...dataRows.map(row => row[index])]
          .map(value => String(value ?? '').length)
          .reduce((max, len) => Math.max(max, len), 10);

        return { wch: Math.min(Math.max(maxLength + 2, 12), 60) };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sanitizeSheetName(event?.title || 'Moduli Iscrizione')
      );

      const fileName = `moduli_iscrizione_${event?.title || 'evento'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      setSuccess('Moduli di iscrizione esportati con successo (Excel)');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Errore export moduli:', err);
      setError('Impossibile esportare i moduli');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Caricamento documenti...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 style={{ color: '#006e39' }}>
                <i className="bi bi-file-earmark-zip me-2"></i>
                Export Documenti e File
              </h2>
              {event && (
                <p className="text-muted mb-0">
                  <i className="bi bi-calendar-event me-2"></i>
                  {event.title}
                </p>
              )}
            </div>
            <div>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate(`/admin/events/${eventId}/participants`)}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Torna ai Partecipanti
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
        </Alert>
      )}

      {/* Export Actions */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
          <h5 className="mb-0">
            <i className="bi bi-download me-2"></i>
            Azioni Export
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Button
                variant="success"
                className="w-100 mb-2"
                onClick={exportAllDocuments}
                disabled={downloading || selectedTeams.length === 0}
                style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
              >
                {downloading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Preparazione...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-zip me-2"></i>
                    Scarica Tutti i File (ZIP)
                  </>
                )}
              </Button>
              <small className="text-muted">
                Include musica, documenti e dettagli squadre
              </small>
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                className="w-100 mb-2"
                onClick={exportRegistrationForms}
                disabled={downloading || selectedTeams.length === 0}
              >
                <i className="bi bi-file-earmark-text me-2"></i>
                Esporta Moduli Iscrizione (Excel)
              </Button>
              <small className="text-muted">
                Esporta i dati di iscrizione in formato Excel (.xlsx)
              </small>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h3 className="mb-1" style={{ color: '#006e39' }}>
                  {selectedTeams.length}
                </h3>
                <small className="text-muted">squadre selezionate</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Teams List */}
      <Card className="border-0 shadow-sm">
        <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-list-check me-2"></i>
              Seleziona Squadre
            </h5>
            <Button
              variant="light"
              size="sm"
              onClick={selectAllTeams}
            >
              {selectedTeams.length === teams.length ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {teams.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">Nessuna squadra registrata per questo evento</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ width: '50px' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTeams.length === teams.length}
                      onChange={selectAllTeams}
                    />
                  </th>
                  <th>Nome Squadra</th>
                  <th>Associazione</th>
                  <th>Membri</th>
                  <th>File Allegati</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => {
                  const files = getTeamFiles(team);
                  return (
                    <tr key={team._id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedTeams.includes(team._id)}
                          onChange={() => toggleTeamSelection(team._id)}
                        />
                      </td>
                      <td>
                        <strong>{team.name}</strong>
                        {team.formData?.choreographyTitle && (
                          <><br />
                          <small className="text-muted">
                            <i className="bi bi-music-note me-1"></i>
                            {team.formData.choreographyTitle}
                          </small></>
                        )}
                      </td>
                      <td>
                        <small>{getAssociationName(team.association_id)}</small>
                      </td>
                      <td>
                        <Badge bg="info">
                          <i className="bi bi-people-fill me-1"></i>
                          {team.members.length}
                        </Badge>
                      </td>
                      <td>
                        {files.length > 0 ? (
                          <div>
                            {files.map((file, idx) => (
                              <Badge key={idx} bg="primary" className="me-1 mb-1">
                                <i className={`bi ${file.type === 'audio' ? 'bi-music-note' : 'bi-file-earmark'} me-1`}></i>
                                {file.filename}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <small className="text-muted">Nessun file</small>
                        )}
                      </td>
                      <td>
                        {files.length > 0 && (
                          <div>
                            {files.map((file, idx) => (
                              <Button
                                key={idx}
                                size="sm"
                                variant="outline-success"
                                className="me-1 mb-1"
                                onClick={() => downloadTeamDocument(team, file)}
                                disabled={downloading}
                                title={`Scarica ${file.filename}`}
                              >
                                <i className="bi bi-download"></i>
                              </Button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TeamDocuments;
